"use client";

import Navbar from "@/components/landing/navbar";
import Footer from "@/components/landing/footer";
import { defaultCountryConfig } from "@/config/counties/default";
import {
  FREE_MONTHLY_CONTACT_CAP,
  INITIAL_MONTHLY_LEAD_CAP,
} from "@/config/stripe-plans";

const CAP = INITIAL_MONTHLY_LEAD_CAP.toLocaleString();
const pledge = defaultCountryConfig.pricing.pledge;

export default function DataHonestyContent() {
  return (
    <div className="min-h-screen bg-card">
      <Navbar data={defaultCountryConfig.navbar} />
      <div className="pt-16">
        <div className="container mx-auto px-4 py-8 sm:py-12 md:py-16 max-w-4xl">
          <div className="mb-12 pb-8 border-b-2 border-primary/20">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-ink mb-4 tracking-tight">
              Data Honesty
            </h1>
            <div className="h-1 w-24 bg-primary rounded-full mb-6" />
            <p className="text-lg text-muted-ink leading-relaxed">
              Most contact tools inflate their &ldquo;verified&rdquo; numbers and bury
              the catch in fine print. We do the opposite: we tell you which emails
              are guessed, verify at export, and state our one limit up front.
            </p>
          </div>

          <div className="prose prose-lg max-w-none text-body space-y-10 leading-relaxed">
            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-ink pb-2 border-b border-line">
                Guessed emails are badged, never sold as verified
              </h2>
              <p>
                When we can&rsquo;t find a real email for a contact, we sometimes
                infer one from their company&rsquo;s known pattern (e.g.{" "}
                <code>first.last@company.com</code>). These are useful, but they are
                not confirmed addresses — so we never mix them in with real ones.
                Every guessed address is:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Badged &ldquo;Guessed&rdquo;</strong> in the results table,
                  never counted as a verified contact.
                </li>
                <li>
                  <strong>Excluded from sender-tool exports by default</strong>{" "}
                  (Instantly, Smartlead, Lemlist) — you opt in explicitly if you want
                  them.
                </li>
                <li>
                  <strong>Flagged in a separate <code>email_guessed</code> column</strong>{" "}
                  in every full export, so it&rsquo;s obvious in your CRM too.
                </li>
              </ul>
              <p>
                That&rsquo;s the opposite of tools that pass pattern-guessed
                addresses off as &ldquo;verified&rdquo; and let you find out at send
                time.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-ink pb-2 border-b border-line">
                What &ldquo;verified&rdquo; actually means here
              </h2>
              <p>
                Before you export, you can verify every email against a live
                mailbox check. Each address comes back as one of:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong className="text-status-ok">Verified</strong> — the mailbox
                  accepts mail; safe to send.
                </li>
                <li>
                  <strong className="text-status-warn">Risky</strong> — a catch-all or
                  unconfirmable domain; send with caution.
                </li>
                <li>
                  <strong className="text-status-bad">Invalid</strong> — the mailbox
                  failed; it will bounce, so we help you drop it.
                </li>
              </ul>
              <p>
                Sender-tool exports default to excluding the risky and invalid
                tiers, so a clean list is the easy path — not an afterthought.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-ink pb-2 border-b border-line">
                Our one limit, in plain sight
              </h2>
              <p>
                We don&rsquo;t run a credit meter. Here is the whole story on
                limits, with nothing hidden:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Free:</strong> {FREE_MONTHLY_CONTACT_CAP} contacts a month,
                  no credit card.
                </li>
                <li>
                  <strong>Unlimited plan ($49.99/mo):</strong> unlimited searches,
                  reveals, and exports, with a fair-use ceiling of {CAP} contacts per
                  month to prevent abuse — raised on request, usually within 24 hours.
                </li>
                <li>
                  <strong>No expiring credits, no per-seat fees, no contracts.</strong>{" "}
                  Re-export any contact you&rsquo;ve found, forever, at no extra cost.
                </li>
              </ul>
            </section>

            {pledge && (
              <section className="space-y-4">
                <h2 className="text-xl sm:text-2xl font-bold text-ink pb-2 border-b border-line">
                  {pledge.title}
                </h2>
                <ul className="list-disc pl-6 space-y-2">
                  {pledge.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
            )}

            <section className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-ink pb-2 border-b border-line">
                Bounce guarantee
              </h2>
              <p>
                We&rsquo;re rolling out a refund-backed bounce guarantee on verified
                exports. If a batch you verified through LeadsMiner bounces above our
                stated threshold, we&rsquo;ll credit back the affected contacts.
                Details are coming soon — in the meantime, verify before you send and
                you&rsquo;ll rarely need it.
              </p>
            </section>
          </div>
        </div>
      </div>
      <Footer data={defaultCountryConfig.footer} />
    </div>
  );
}
